import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, AlertCircle, Wrench, Ban, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { getClassrooms, createClassroom, updateClassroom } from '../../../hooks/controlEscolarService';
import type { Classroom, ClassroomStatus } from '../../../hooks/controlEscolarTypes';

interface Equipment {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
}

interface SuggestedClassroom {
  id: number;
  name: string;
  capacity: number | null;
  location: string | null;
  status: string;
  _conflict?: boolean;
}

interface AffectedSchedule {
  id: number;
  dia: string | null;
  startTime: string;
  endTime: string;
  periodId: number;
  courseName: string | null;
  programName: string | null;
  periodName: string | null;
  periodStatus: string | null;
  suggestions: SuggestedClassroom[];
}

interface MaintenanceImpact {
  affectedCount: number;
  schedules: AffectedSchedule[];
}

const DAY_LABELS: Record<string, string> = {
  lunes: 'Lunes', martes: 'Martes', miercoles: 'Miércoles', miércoles: 'Miércoles',
  jueves: 'Jueves', viernes: 'Viernes', sabado: 'Sábado', sábado: 'Sábado', domingo: 'Domingo',
};

export default function Aulas() {
  const navigate = useNavigate();
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingClassroom, setEditingClassroom] = useState<Classroom | null>(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Maintenance reassignment state
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [maintenanceImpact, setMaintenanceImpact] = useState<MaintenanceImpact | null>(null);
  const [maintenanceClassroomName, setMaintenanceClassroomName] = useState('');
  const [reassignments, setReassignments] = useState<Record<number, number | null>>({});
  const [applyingReassign, setApplyingReassign] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    capacity: 20,
    location: '',
    status: 'activa' as ClassroomStatus,
    equipmentIds: [] as number[]
  });

  useEffect(() => {
    loadClassrooms();
    loadEquipments();
  }, []);

  const loadClassrooms = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getClassrooms();
      const sorted = Array.isArray(data) ? data.sort((a: Classroom, b: Classroom) => a.name.localeCompare(b.name, 'es')) : [];
      setClassrooms(sorted);
    } catch (err) {
      console.error('Error cargando aulas:', err);
      setError('Error al cargar las aulas');
      setClassrooms([]);
    } finally {
      setLoading(false);
    }
  };

  const loadEquipments = async () => {
    try {
      const response = await fetch('/api/equipments', { credentials: 'include' });
      if (!response.ok) throw new Error('Error al cargar equipamientos');
      const data = await response.json();
      setEquipments(Array.isArray(data) ? data.filter((e: Equipment) => e.isActive) : []);
    } catch (err) {
      console.error('Error cargando equipamientos:', err);
    }
  };

  const handleOpenModal = (classroom?: Classroom) => {
    if (classroom) {
      setEditingClassroom(classroom);
      setFormData({
        name: classroom.name,
        capacity: classroom.capacity,
        location: classroom.location || '',
        status: classroom.status,
        equipmentIds: (classroom as any).equipments?.map((e: Equipment) => e.id) || []
      });
    } else {
      setEditingClassroom(null);
      setFormData({ name: '', capacity: 20, location: '', status: 'activa', equipmentIds: [] });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingClassroom(null);
    setFormData({ name: '', capacity: 20, location: '', status: 'activa', equipmentIds: [] });
  };

  const handleToggleEquipment = (equipmentId: number) => {
    setFormData(prev => ({
      ...prev,
      equipmentIds: prev.equipmentIds.includes(equipmentId)
        ? prev.equipmentIds.filter(id => id !== equipmentId)
        : [...prev.equipmentIds, equipmentId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { alert('El nombre del aula es requerido'); return; }
    if (formData.capacity < 1) { alert('La capacidad debe ser mayor a 0'); return; }
    try {
      setSaving(true);
      if (editingClassroom) {
        const { status, ...dataWithoutStatus } = formData;
        await updateClassroom(editingClassroom.id, dataWithoutStatus);
      } else {
        await createClassroom(formData);
      }
      await loadClassrooms();
      handleCloseModal();
    } catch (err) {
      console.error('Error guardando aula:', err);
      alert('Error al guardar el aula');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeStatus = async (classroom: Classroom, newStatus: ClassroomStatus) => {
    const label = statusLabels[newStatus];

    // For maintenance status change, show a richer flow
    const confirmMsg = newStatus === 'mantenimiento'
      ? `¿Poner el aula "${classroom.name}" en Mantenimiento?\n\nSe buscará si hay clases programadas que necesiten reasignación.`
      : `¿Cambiar el estado del aula "${classroom.name}" a "${label}"?`;

    if (!confirm(confirmMsg)) return;

    try {
      const response = await fetch(`/api/classrooms/${classroom.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Error al cambiar el estado');
      }

      const data = await response.json();
      await loadClassrooms();

      // If there are affected schedules, open the reassignment modal
      if (newStatus === 'mantenimiento' && data.maintenanceImpact && data.maintenanceImpact.affectedCount > 0) {
        const impact: MaintenanceImpact = data.maintenanceImpact;
        // Pre-select the first available (conflict-free) suggestion for each schedule
        const initialReassignments: Record<number, number | null> = {};
        for (const sched of impact.schedules) {
          const firstFree = sched.suggestions.find(s => !s._conflict);
          initialReassignments[sched.id] = firstFree?.id ?? null;
        }
        setMaintenanceImpact(impact);
        setMaintenanceClassroomName(classroom.name);
        setReassignments(initialReassignments);
        setShowMaintenanceModal(true);
      } else if (newStatus === 'mantenimiento' && (!data.maintenanceImpact || data.maintenanceImpact.affectedCount === 0)) {
        // No affected schedules — status changed cleanly
        alert(`✅ El aula "${classroom.name}" está en Mantenimiento.\n\nNo hay horarios activos asignados a esta aula.`);
      }
    } catch (err: any) {
      console.error('Error cambiando estado:', err);
      alert(err.message || 'Error al cambiar el estado del aula');
    }
  };

  const handleApplyReassignments = async () => {
    if (!maintenanceImpact) return;

    const toSend = maintenanceImpact.schedules.map(sched => ({
      scheduleId: sched.id,
      newClassroomId: reassignments[sched.id] ?? null,
    }));

    try {
      setApplyingReassign(true);
      const response = await fetch('/api/classrooms/bulk-reassign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reassignments: toSend }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Error al reasignar horarios');
      }

      const result = await response.json();
      const errors = (result.results || []).filter((r: any) => r.status === 'error');

      if (errors.length > 0) {
        alert(`Reasignación completada con ${errors.length} error(es):\n${errors.map((e: any) => e.message).join('\n')}`);
      } else {
        alert(`✅ ${result.successCount} horario(s) reasignado(s) correctamente.`);
      }

      setShowMaintenanceModal(false);
      setMaintenanceImpact(null);
    } catch (err: any) {
      alert(err.message || 'Error al aplicar reasignaciones');
    } finally {
      setApplyingReassign(false);
    }
  };

  const handleSkipReassignments = () => {
    setShowMaintenanceModal(false);
    setMaintenanceImpact(null);
  };

  const statusColors: Record<ClassroomStatus, string> = {
    activa: 'bg-green-100 text-green-800',
    mantenimiento: 'bg-yellow-100 text-yellow-800',
    inhabilitada: 'bg-red-100 text-red-800'
  };

  const statusLabels: Record<ClassroomStatus, string> = {
    activa: 'Activa',
    mantenimiento: 'Mantenimiento',
    inhabilitada: 'Inhabilitada'
  };

  const statusIcons: Record<ClassroomStatus, any> = {
    activa: AlertCircle,
    mantenimiento: Wrench,
    inhabilitada: Ban
  };

  const assignedCount = maintenanceImpact
    ? maintenanceImpact.schedules.filter(s => reassignments[s.id] != null).length
    : 0;

  return (
    <div className="p-6">
      <button
        onClick={() => navigate('/app/catalogos')}
        className="mb-4 flex items-center text-gray-600 hover:text-gray-900 font-medium"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Volver a Catálogos
      </button>

      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catálogo de Aulas</h1>
          <p className="text-gray-600">Gestión de aulas y espacios académicos</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          + Nueva Aula
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="mb-4">
        <input
          type="text"
          placeholder="Buscar por nombre, ubicación..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando aulas...</div>
      ) : classrooms.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No hay aulas registradas</p>
          <p className="text-gray-400 text-sm mt-2">Comienza agregando tu primera aula</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aula</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Capacidad</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Equipamientos</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ubicación</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {classrooms
                .filter((classroom) =>
                  classroom.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  (classroom.location || '').toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((classroom) => {
                  const safeStatus = classroom.status || 'activa';
                  const StatusIcon = statusIcons[safeStatus] ?? AlertCircle;
                  const statusColor = statusColors[safeStatus] ?? 'bg-green-100 text-green-800';
                  const statusLabel = statusLabels[safeStatus] ?? 'Activa';

                  return (
                    <tr key={classroom.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{classroom.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{classroom.capacity} personas</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(classroom as any).equipments && (classroom as any).equipments.length > 0 ? (
                            (classroom as any).equipments.map((eq: Equipment) => (
                              <span key={eq.id} className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full">
                                {eq.name}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-400">Sin equipamientos</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{classroom.location || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${statusColor}`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusLabel}
                          </span>
                          <select
                            value={safeStatus}
                            onChange={(e) => handleChangeStatus(classroom, e.target.value as ClassroomStatus)}
                            className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="activa">Activa</option>
                            <option value="mantenimiento">Mantenimiento</option>
                            <option value="inhabilitada">Inhabilitada</option>
                          </select>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleOpenModal(classroom)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create / Edit Classroom Modal ─────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingClassroom ? 'Editar Aula' : 'Nueva Aula'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Aula *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ej: Aula 101"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Capacidad *</label>
                  <input
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Equipamientos</label>
                  <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                    {equipments.length === 0 ? (
                      <p className="text-sm text-gray-500">No hay equipamientos disponibles</p>
                    ) : (
                      <div className="space-y-2">
                        {equipments.map((equipment) => (
                          <label key={equipment.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={formData.equipmentIds.includes(equipment.id)}
                              onChange={() => handleToggleEquipment(equipment.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-900">{equipment.name}</span>
                            {equipment.description && (
                              <span className="text-xs text-gray-500">({equipment.description})</span>
                            )}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  {formData.equipmentIds.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {formData.equipmentIds.map(id => {
                        const eq = equipments.find(e => e.id === id);
                        return eq ? (
                          <span key={id} className="inline-flex items-center px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full">
                            {eq.name}
                            <button type="button" onClick={() => handleToggleEquipment(id)} className="ml-1 hover:text-amber-900">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ej: Edificio A, Primer Piso"
                  />
                </div>

                {!editingClassroom && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estado Inicial</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as ClassroomStatus })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="activa">Activa</option>
                      <option value="mantenimiento">Mantenimiento</option>
                      <option value="inhabilitada">Inhabilitada</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">El estado puede cambiarse después desde la tabla usando el dropdown de cada aula</p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button type="button" onClick={handleCloseModal} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" disabled={saving}>
                  {saving ? 'Guardando...' : editingClassroom ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Maintenance Reassignment Modal ────────────────────────────────── */}
      {showMaintenanceModal && maintenanceImpact && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            {/* Header */}
            <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-4 flex items-start gap-3">
              <Wrench className="h-6 w-6 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-lg font-bold text-yellow-900">
                  Aula en Mantenimiento — Reasignación de Horarios
                </h2>
                <p className="text-sm text-yellow-700 mt-1">
                  El aula <strong>{maintenanceClassroomName}</strong> tiene{' '}
                  <strong>{maintenanceImpact.affectedCount} clase(s)</strong> programadas.
                  Selecciona un aula alternativa para cada una o decide después.
                </p>
              </div>
              <button onClick={handleSkipReassignments} className="text-yellow-600 hover:text-yellow-900 flex-shrink-0">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Progress indicator */}
            <div className="px-6 py-2 bg-yellow-50 border-b border-yellow-100 flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-gray-700">
                <strong className="text-green-700">{assignedCount}</strong> de{' '}
                <strong>{maintenanceImpact.affectedCount}</strong> asignadas
              </span>
              {assignedCount < maintenanceImpact.affectedCount && (
                <span className="text-yellow-600 ml-2">
                  · {maintenanceImpact.affectedCount - assignedCount} pendiente(s)
                </span>
              )}
            </div>

            {/* Schedule list */}
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
              {maintenanceImpact.schedules.map((sched, idx) => {
                const freeOptions = sched.suggestions.filter(s => !s._conflict);
                const busyOptions = sched.suggestions.filter(s => s._conflict);
                const selectedId = reassignments[sched.id];
                const isAssigned = selectedId != null;

                return (
                  <div
                    key={sched.id}
                    className={`rounded-lg border p-4 ${isAssigned ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                            #{idx + 1}
                          </span>
                          <span className="text-sm font-semibold text-gray-900 truncate">
                            {sched.courseName || 'Materia sin nombre'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 space-y-0.5">
                          <div>
                            📚 <strong>Programa:</strong> {sched.programName || '—'}
                          </div>
                          <div>
                            📅 <strong>Período:</strong> {sched.periodName || '—'}
                            {sched.periodStatus && (
                              <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 rounded">
                                {sched.periodStatus}
                              </span>
                            )}
                          </div>
                          <div>
                            🕐 <strong>Horario:</strong>{' '}
                            {sched.dia ? (DAY_LABELS[sched.dia.toLowerCase()] || sched.dia) : '—'}{' '}
                            {sched.startTime}–{sched.endTime}
                          </div>
                        </div>
                      </div>

                      <div className="flex-shrink-0 w-56">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Aula alternativa
                        </label>
                        <select
                          value={selectedId ?? ''}
                          onChange={(e) => setReassignments(prev => ({
                            ...prev,
                            [sched.id]: e.target.value ? parseInt(e.target.value) : null,
                          }))}
                          className={`w-full text-sm border rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isAssigned ? 'border-green-400 bg-white' : 'border-gray-300 bg-white'
                          }`}
                        >
                          <option value="">— Sin asignar —</option>
                          {freeOptions.length > 0 && (
                            <optgroup label="✅ Disponibles en ese horario">
                              {freeOptions.map(c => (
                                <option key={c.id} value={c.id}>
                                  {c.name}{c.capacity ? ` (cap. ${c.capacity})` : ''}{c.location ? ` · ${c.location}` : ''}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {busyOptions.length > 0 && (
                            <optgroup label="⚠️ Con posible conflicto">
                              {busyOptions.map(c => (
                                <option key={c.id} value={c.id}>
                                  {c.name}{c.capacity ? ` (cap. ${c.capacity})` : ''}{c.location ? ` · ${c.location}` : ''}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {sched.suggestions.length === 0 && (
                            <option disabled>Sin aulas activas disponibles</option>
                          )}
                        </select>
                        {isAssigned && (
                          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Asignada
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-white flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                <span>Las clases no reasignadas quedan marcadas como <em>pendientes de reasignación</em>.</span>
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <button
                  onClick={handleSkipReassignments}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
                  disabled={applyingReassign}
                >
                  Decidir después
                </button>
                <button
                  onClick={handleApplyReassignments}
                  disabled={applyingReassign || assignedCount === 0}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
                >
                  {applyingReassign ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Aplicando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      Aplicar {assignedCount > 0 ? `${assignedCount} reasignación(es)` : 'reasignaciones'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
